import { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface CurrencyInputProps {
  id?: string;
  value: number | string;
  onChange: (rawValue: string) => void;
  min?: number;
  max?: number;
  className?: string;
  placeholder?: string;
  prefix?: string;
  allowNegative?: boolean;
}

/**
 * Currency input that displays US-formatted numbers (with commas)
 * while storing raw numeric values.
 */
export function CurrencyInput({
  id,
  value,
  onChange,
  min,
  max,
  className,
  placeholder = '0',
  prefix = '$',
  allowNegative = false,
}: CurrencyInputProps) {
  const formatDisplay = useCallback((val: number | string): string => {
    if (val === '' || val === undefined || val === null) return '';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  }, []);

  const [displayValue, setDisplayValue] = useState(() => formatDisplay(value));

  useEffect(() => {
    // Only update display if value changed externally
    const numericDisplay = displayValue.replace(/,/g, '');
    const currentNum = numericDisplay === '' ? '' : numericDisplay;
    const incomingNum = value === '' || value === undefined || value === null ? '' : String(value);
    if (currentNum !== incomingNum) {
      setDisplayValue(formatDisplay(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, '');
    
    // Allow empty
    if (raw === '' || raw === '-') {
      setDisplayValue(raw);
      onChange('');
      return;
    }

    // Validate numeric
    const pattern = allowNegative ? /^-?\d*$/ : /^\d*$/;
    if (!pattern.test(raw)) return;

    const num = parseInt(raw);
    if (isNaN(num)) return;
   if (min !== undefined && num < min && raw !== '') return;
    if (max !== undefined && num > max) return;

    setDisplayValue(num.toLocaleString('en-US'));
    onChange(raw);
  };

  const handleBlur = () => {
    if (displayValue === '' || displayValue === '-') return;
    const raw = displayValue.replace(/,/g, '');
    const num = parseInt(raw);
    if (!isNaN(num)) {
      setDisplayValue(num.toLocaleString('en-US'));
    }
  };

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
          {prefix}
        </span>
      )}
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(prefix ? 'pl-7' : '', className)}
      />
    </div>
  );
}
